/**
 * MonthlyBillCalendar — Printable bill calendar
 *
 * Renders a 7-column calendar grid (Sun–Sat) with bills placed on their
 * due dates. Each bill entry has a checkbox square for pen-marking on
 * the printed version. Paid bills show green with strikethrough.
 *
 * Phase 9A
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveBills } from '../../services/db';
import { formatCurrency, formatDate, getMonthKey, shiftMonthKey, formatMonthLabel } from '../../utils/format';

export default function MonthlyBillCalendar() {
  const { user } = useAuth();
  const [monthKey, setMonthKey] = useState(getMonthKey());
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const b = await getActiveBills(user.id);
      setBills(b);
    } catch (err) {
      console.error('Failed to load bill calendar:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Parse monthKey
  const [year, month] = monthKey.split('-').map(Number);
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month, 0).getDate();

  // Build bill map: day → [bills]
  const billsByDay = {};
  for (const bill of bills) {
    const day = bill.due_day;
    if (day >= 1 && day <= daysInMonth) {
      if (!billsByDay[day]) billsByDay[day] = [];
      billsByDay[day].push(bill);
    }
  }

  // Build calendar grid cells
  const cells = [];
  const totalCells = Math.ceil((firstDayOfWeek + daysInMonth) / 7) * 7;
  const prevMonthDays = new Date(year, month - 1, 0).getDate();

  const today = new Date();
  const todayDay = today.getDate();
  const todayMonth = today.getMonth() + 1;
  const todayYear = today.getFullYear();

  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - firstDayOfWeek + 1;
    if (dayNum < 1) {
      // Previous month
      cells.push({ day: prevMonthDays + dayNum, outside: true, bills: [] });
    } else if (dayNum > daysInMonth) {
      // Next month
      cells.push({ day: dayNum - daysInMonth, outside: true, bills: [] });
    } else {
      const isToday = dayNum === todayDay && month === todayMonth && year === todayYear;
      cells.push({ day: dayNum, outside: false, isToday, bills: billsByDay[dayNum] || [] });
    }
  }

  // Totals
  const totalBills = bills.reduce((s, b) => s + (b.amount || 0), 0);
  const paidTotal = bills.filter(b => b.current_cycle_paid).reduce((s, b) => s + (b.amount || 0), 0);
  const remainingTotal = totalBills - paidTotal;

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return <div className="report-content"><p className="empty-state">Loading calendar...</p></div>;
  }

  return (
    <div className="report-content">
      {/* Print-only header */}
      <div className="report-header">
        <div className="report-header-title">Spendable — Bill Calendar</div>
        <div className="report-header-subtitle">{formatMonthLabel(monthKey)}</div>
      </div>

      {/* Month selector (hidden in print) */}
      <div className="month-selector">
        <button onClick={() => setMonthKey(k => shiftMonthKey(k, -1))}>&lsaquo;</button>
        <span className="month-label">{formatMonthLabel(monthKey)}</span>
        <button onClick={() => setMonthKey(k => shiftMonthKey(k, 1))}>&rsaquo;</button>
      </div>

      {/* Calendar grid */}
      <div className="report-calendar">
        {dayNames.map(d => (
          <div key={d} className="report-calendar-header">{d}</div>
        ))}
        {cells.map((cell, i) => (
          <div
            key={i}
            className={`report-calendar-cell${cell.outside ? ' outside-month' : ''}${cell.isToday ? ' today' : ''}`}
          >
            <div className="report-calendar-day">{cell.day}</div>
            {cell.bills.map(bill => (
              <div key={bill.id} className={`report-calendar-bill${bill.current_cycle_paid ? ' paid' : ''}`}>
                <div className="report-calendar-checkbox" />
                <span>{bill.name} {formatCurrency(bill.amount, false)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Monthly totals */}
      <div className="report-calendar-totals">
        <div className="report-calendar-total-item">
          <div className="report-calendar-total-label">Total</div>
          <div className="report-calendar-total-value">{formatCurrency(totalBills, false)}</div>
        </div>
        <div className="report-calendar-total-item">
          <div className="report-calendar-total-label">Paid</div>
          <div className="report-calendar-total-value success">{formatCurrency(paidTotal, false)}</div>
        </div>
        <div className="report-calendar-total-item">
          <div className="report-calendar-total-label">Remaining</div>
          <div className="report-calendar-total-value danger">{formatCurrency(remainingTotal, false)}</div>
        </div>
      </div>

      <div className="report-footer">
        Generated {formatDate(new Date(), 'full')}
      </div>
    </div>
  );
}
